import { readFileSync, writeFileSync, readdirSync, existsSync } from "node:fs";
import { dirname, join } from "node:path";
import { homedir } from "node:os";

/**
 * Fork a Claude Code session JSONL by truncating BEFORE a chosen user-turn
 * message UUID and returning the dropped user message's text so the caller
 * can prefill the new agent's composer with it.
 *
 *   parent JSONL:  ... | a3 | u4 (chosen) | a4 | tr1 | a5 | u5 | a6 | ...
 *   forked JSONL:  ... | a3
 *   droppedUserText: text of u4
 *
 * The user message you "fork from" is removed from the new conversation —
 * the new agent inherits the prior context only, and the user message text
 * lands in the composer so you can edit and re-send it. This matches the
 * mental model of "rewind to before this message and try again".
 *
 * Forking the very first user turn is rejected (would produce an empty
 * conversation; equivalent to creating a fresh agent).
 *
 * Note on file safety: we read flushed lines synchronously from the parent
 * file. Reading [0, forkLineIndex) does not race with the parent session
 * still appending new lines past that point — POSIX append-only writes do
 * not affect already-flushed bytes — so it is safe to fork while the parent
 * is still running.
 */

export class ForkPointInvalidError extends Error {
  constructor(
    public readonly reason:
      | "not_found"
      | "not_user_turn"
      | "empty_session"
      | "tool_result"
      | "first_user_turn",
    public readonly forkPointUuid: string,
  ) {
    super(`Invalid fork point (${reason}): ${forkPointUuid}`);
    this.name = "ForkPointInvalidError";
  }
}

// Tool_result payloads ride on type:"user" entries in Claude's transcript.
// Forking at one of those would split a tool_call/tool_result pair and leave
// the resumed session in a half-finished tool loop.
function jsonLineIsToolResultUser(json: { message?: unknown }): boolean {
  if (typeof json.message !== "object" || json.message === null) return false;
  const content = (json.message as { content?: unknown }).content;
  if (!Array.isArray(content)) return false;
  return content.some(
    (c) => typeof c === "object" && c !== null && (c as { type?: unknown }).type === "tool_result",
  );
}

// Pull the visible text out of a Claude user-message payload. Mirrors the
// renderer's behavior: string content is returned as-is, array content is
// joined from text blocks. Image/tool-use blocks are ignored — attachments
// don't ride on the fork text path (the client carries them separately
// from the optimistic stream item).
function extractUserMessageText(message: unknown): string {
  if (typeof message !== "object" || message === null) return "";
  const content = (message as { content?: unknown }).content;
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  const parts: string[] = [];
  for (const block of content) {
    if (typeof block !== "object" || block === null) continue;
    const text = (block as { text?: unknown }).text;
    if (typeof text === "string" && text.length > 0) parts.push(text);
  }
  return parts.join("\n");
}

export interface ForkSessionInput {
  sourceSessionPath: string;
  forkPointUuid: string;
  newSessionId: string;
  targetSessionPath: string;
}

export interface ForkSessionResult {
  retainedLineCount: number;
  forkPointLineIndex: number;
  /** Text of the user message that was dropped — prefilled into the new agent's composer. */
  droppedUserText: string;
}

/**
 * Locate a Claude Code session file by sessionId alone. Scans every project
 * slug directory under ~/.claude/projects/ — cheaper than reverse-engineering
 * Claude's cwd-slug encoding.
 */
export function findClaudeSessionFile(params: {
  sessionId: string;
  claudeHomeDir?: string;
}): string | null {
  const home = params.claudeHomeDir ?? join(homedir(), ".claude");
  const projectsDir = join(home, "projects");
  if (!existsSync(projectsDir)) return null;

  for (const entry of readdirSync(projectsDir, { withFileTypes: true })) {
    if (!entry.isDirectory()) continue;
    const candidate = join(projectsDir, entry.name, `${params.sessionId}.jsonl`);
    if (existsSync(candidate)) return candidate;
  }
  return null;
}

export function forkSession(input: ForkSessionInput): ForkSessionResult {
  const raw = readFileSync(input.sourceSessionPath, "utf-8");
  const lines = raw.split("\n");

  // Walk once: find the fork point's line index, validate it's a real user
  // turn, count how many real user turns precede it (to enforce the
  // first-user-turn block), and capture the dropped user message text.
  let forkLineIndex = -1;
  let droppedUserText = "";
  let realUserTurnsBeforeFork = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (line.length === 0) continue;
    let json: { type?: unknown; uuid?: unknown; message?: unknown } | null = null;
    try {
      json = JSON.parse(line) as { type?: unknown; uuid?: unknown; message?: unknown };
    } catch {
      continue;
    }
    if (json.uuid === input.forkPointUuid) {
      if (json.type !== "user") {
        throw new ForkPointInvalidError("not_user_turn", input.forkPointUuid);
      }
      if (jsonLineIsToolResultUser(json)) {
        throw new ForkPointInvalidError("tool_result", input.forkPointUuid);
      }
      forkLineIndex = i;
      droppedUserText = extractUserMessageText(json.message);
      break;
    }
    // Track real user turns before the fork point so we can reject forking
    // the first user message (would produce an empty conversation).
    if (json.type === "user" && !jsonLineIsToolResultUser(json)) {
      realUserTurnsBeforeFork += 1;
    }
  }

  if (forkLineIndex === -1) {
    // Distinguish "empty parseable session" from "uuid simply absent" for
    // slightly better error messaging.
    const hasAnyUuid = lines.some((l) => {
      if (!l) return false;
      try {
        return typeof (JSON.parse(l) as { uuid?: unknown }).uuid === "string";
      } catch {
        return false;
      }
    });
    throw new ForkPointInvalidError(
      hasAnyUuid ? "not_found" : "empty_session",
      input.forkPointUuid,
    );
  }

  if (realUserTurnsBeforeFork === 0) {
    throw new ForkPointInvalidError("first_user_turn", input.forkPointUuid);
  }

  // Truncate: copy lines strictly BEFORE the fork point. The chosen user
  // message and everything after it (its assistant response, subsequent
  // turns) are dropped — the user message text is returned separately for
  // composer prefill.
  const copied = lines.slice(0, forkLineIndex).filter((l) => l.length > 0);

  const targetDir = dirname(input.targetSessionPath);
  if (!existsSync(targetDir)) {
    throw new Error(`Target directory does not exist: ${targetDir}`);
  }
  const payload = copied.length > 0 ? copied.join("\n") + "\n" : "";
  writeFileSync(input.targetSessionPath, payload, "utf-8");

  return {
    retainedLineCount: copied.length,
    forkPointLineIndex: forkLineIndex,
    droppedUserText,
  };
}
